<template>
  <DialogComponent
    :modelValue="true"
    :cancelModalOnClickAway="false"
    @close="cancel"
    class="cursor-default">
    <Form
      @submit="save"
      v-slot="{ errors }">
      <div>
        <h1
          :id="'authorInformationModal'"
          class="font-bitter text-3xl font-bold mb-4">
          Author information
        </h1>
        <h2 class="font-roboto text-2xl font-bold mb-4">
          Contact information
        </h2>
        <div class="w-full space-y-8">
          <Field
            :rules="`required:You must enter a first name|max-characters-length:${limits.authors.characters.firstName},Author first name must not exceed`"
            name="firstName"
            v-slot="{ field }"
            :value="editedAuthor.firstName">
            <FormInput
              id="firstName"
              data-testid="first-name-author"
              v-model="editedAuthor.firstName"
              label="First name"
              :error-message="errors.firstName ? errors.firstName : ''"
              v-bind="field"
              class="data-hj-suppress"
            />
          </Field>
          <div>
            <Field
              :value="editedAuthor.initials"
              :rules="`max-characters-length:${limits.authors.characters.initials},Author initials must not exceed`"
              v-slot="{ field }"
              name="initials">
              <div class="[&>div]:w-full [&>div>div:nth-child(1)]:w-1/2 [&>div>div:nth-child(1)]:md:w-1/4 [&>div>div:nth-child(2)]:w-1/2 [&>div>div:nth-child(2)]:md:w-1/4">
                <FormInput
                  id="initials"
                  v-model.trim="editedAuthor.initials"
                  label="Initial(s)"
                  :errorMessage="errors.initials ? errors.initials : ''"
                  isOptional
                  v-bind="field"
                  class="data-hj-suppress"
                />
              </div>
            </Field>
          </div>
          <Field
            :rules="`required:You must enter a last name|max-characters-length:${limits.authors.characters.lastName},Author last name must not exceed`"
            name="lastName"
            v-slot="{ field }"
            :value="editedAuthor.lastName">
            <FormInput
              id="lastName"
              data-testid="last-name-author"
              label="Last name"
              :error-message="errors.lastName ? errors.lastName : ''"
              v-model="editedAuthor.lastName"
              v-bind="field"
              class="data-hj-suppress"
            />
          </Field>
          <Field
            :rules="`email|not-duplicated-mail-edit-author:${editedAuthor.id}|not-duplicated-mail-edit-reviewer|max-characters-length:${limits.authors.characters.email},Email address must not exceed`"
            name="email"
            v-slot="{ field }"
            :value="editedAuthor.email">
            <FormInput
              id="email"
              data-testid="email-author"
              type="email"
              label="Email address"
              :error-message="errors.email ? errors.email : ''"
              v-model="editedAuthor.email"
              v-bind="field"
              class="data-hj-suppress"/>
          </Field>
          <h2 class="font-roboto text-2xl font-bold my-8 mb-4">
            Affiliations
          </h2>
          <p>
            Add the primary institute this author is affiliated with on this manuscript.
          </p>
          <div class="w-full space-y-8">
            <Field
              v-if="hasInstitutes"
              name="institute"
              v-model="selectedInstituteIndex"
              v-slot="{ field: fieldProps, errorMessage: instituteError }">
              <FormSelect
                v-bind="fieldProps"
                :options="selectInstituteOptions"
                label="Choose Institute"
                helpText="Choose an existing institute, or add a new one below."
                :error-message="instituteError"
                v-model="selectedInstituteIndex"
                id="institute"
                data-hj-suppress
                isOptional
              />
            </Field>
            <Field
              name="instituteName"
              :rules="`required:You must enter an institute name|max-characters-length:${limits.authors.characters.institute},Institute / organisation must not exceed`"
              v-model="editedAuthor.affiliation.name"
              v-slot="{ errorMessage }">
              <FormInput
                data-testid="affiliated-institute-name"
                id="instituteName"
                label="Institute / Organisation"
                v-model="editedAuthor.affiliation.name"
                :error-message="errorMessage"/>
            </Field>
            <Field
              name="instituteCity"
              :rules="`required:You must enter an institute city|max-characters-length:${limits.authors.characters.city},City / town must not exceed`"
              v-model="editedAuthor.affiliation.city"
              v-slot="{ errorMessage }">
              <FormInput
                data-testid="affiliated-institute-city"
                id="instituteCity"
                label="City / Town"
                v-model="editedAuthor.affiliation.city"
                :error-message="errorMessage"
              />
            </Field>
            <Field
              :rules="'required'"
              name="instituteCountry"
              v-model="selectedCountryId"
              v-slot="{ field: fieldProps, errorMessage: countryError }">
              <FormSelect
                :modelValue="fieldProps.value"
                @update:modelValue="fieldProps.onInput"
                id="instituteCountry"
                :options="countryOptions"
                label="Country"
                :error-message="countryError ? 'You must enter an institute country' : ''"
              />
            </Field>
          </div>
          <ButtonComponent
            type="submit"
            :label="saveButtonText"
            variant="primary"
            data-testid="update-author-details"
            class="w-full md:w-auto"
            id="updateAuthor" />
          <ButtonComponent
            label="Delete this author"
            variant="tertiary"
            id="deleteAuthor"
            class="w-full md:w-auto mt-2"
            data-testid="delete-author-btn"
            v-if="canDelete"
            @customClick="deleteAuthor" />
        </div>
      </div>
    </Form>
  </DialogComponent>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch, nextTick } from 'vue'
import { Author, Country, Institute, Submission } from '@/types'
import Guid from '../../utils/Guid'
import { useStore } from 'vuex'
import { distinct, indexOfByPredicate } from '../../store/functions'
import { Form, Field, useForm } from 'vee-validate'
import ButtonComponent from '../ui-components/ButtonComponent.vue'
import deepClone from '../../services/deepClone'
import FormInput from '../ui-components/FormInput.vue'
import FormSelect from '../ui-components/FormSelect.vue'
import DialogComponent from '../ui-components/DialogComponent.vue'
import countriesList from '../../data/countries.json'
import limits from '../../constants/limits'

const store = useStore()
const { setFieldValue, validate } = useForm()
const emit = defineEmits(['saved', 'cancel', 'deleteAuthor'])
const props = defineProps({
  author: { required: false, type: Object as () => Author },
})

const editedAuthor = reactive<Author>(
  props.author
    ? deepClone(props.author)
    : {
        id: Guid.generate(),
        firstName: '',
        initials: '',
        lastName: '',
        isCorresponding: false,
        email: '',
        orcid: '',
        affiliation: {
          name: '',
          city: '',
          country: undefined,
        },
      },
) as Author

const selectedInstituteIndex = ref<number | null>(null)
// Maintain country select value as a string to match FormSelect option values
const selectedCountryId = ref('')
const institutes = ref<Array<Institute>>([])
const countries = ref<Array<Country>>(
  [...(countriesList as Country[])].sort((a, b) => a.name.localeCompare(b.name)),
)

const hasInstitutes = computed(() => institutes.value.length > 0)

const selectInstituteOptions = computed(() =>
  institutes.value.map((institute, index) => ({
    text: `${institute.name}, ${institute.city}, ${institute.country?.name}`,
    value: index,
  })),
)

const countryOptions = computed(() =>
  countries.value.map(country => ({
    text: country.name,
    value: country.id,
  })),
)

const saveButtonText = computed(() => (props.author ? 'Update' : 'Add'))

watch(
  () => editedAuthor.affiliation?.country?.id ?? '',
  (id) => {
    if (selectedCountryId.value !== id) {
      selectedCountryId.value = id
    }
  },
  { immediate: true, flush: 'sync' },
)

watch(selectedCountryId, (id) => {
  if (!id) {
    editedAuthor.affiliation.country = undefined
    return
  }

  const nextCountry = countries.value.find(country => country.id === id)

  if (nextCountry && editedAuthor.affiliation.country?.id !== nextCountry.id) {
    editedAuthor.affiliation.country = nextCountry
  }
})

watch(selectedInstituteIndex, async (index) => {
  if (index === null || index < 0) {
    return
  }

  const selectedInstitute = institutes.value[index]

  if (!selectedInstitute) {
    return
  }

  editedAuthor.affiliation = {
    ...editedAuthor.affiliation,
    ...selectedInstitute,
  }

  // Ensure vee-validate Field values updated for institute-derived fields
  setFieldValue('instituteName', selectedInstitute.name || '')
  setFieldValue('instituteCity', selectedInstitute.city || '')
  const countryId = selectedInstitute.country?.id ? String(selectedInstitute.country.id) : ''
  setFieldValue('instituteCountry', countryId)
  selectedCountryId.value = countryId

  // Force validation update after institute selection
  await nextTick()
  if (validate) {
    await validate()
  }
})

onMounted(async () => {
  const submission = store.state.activeSubmission as Submission
  const affiliationTransformation = (affilitation: Institute) => `${affilitation.name}|${affilitation.city}|${affilitation.country?.name}`
  const foundInstitutes = distinct(
    submission.authors
      .map(author => author.affiliation)
      .filter(affiliation => affiliation?.country),
    affiliationTransformation,
  ) as Institute[]

  institutes.value = foundInstitutes

  const transformedAffiliation = affiliationTransformation(editedAuthor.affiliation as Institute)
  const matchedIndex = indexOfByPredicate(
    foundInstitutes,
    (affiliation: Institute) => affiliationTransformation(affiliation) === transformedAffiliation,
  )

  selectedInstituteIndex.value = typeof matchedIndex === 'number' && matchedIndex >= 0 ? matchedIndex : null
})

function save() {
  formatEditedAuthor(editedAuthor)
  emit('saved', editedAuthor)
}

function cancel() {
  emit('cancel')
}

function deleteAuthor() {
  emit('deleteAuthor')
}

function formatEditedAuthor(editedAuthor: Author) {
  editedAuthor.firstName = editedAuthor.firstName.trim()
  editedAuthor.lastName = editedAuthor.lastName.trim()
  editedAuthor.email = editedAuthor.email.trim()
  editedAuthor.initials = editedAuthor.initials?.trim()
  editedAuthor.affiliation.name = editedAuthor.affiliation.name.trim()
  editedAuthor.affiliation.city = editedAuthor.affiliation.city.trim()
}

const canDelete = computed((): boolean => {
  return Array.isArray(store.state.activeSubmission.authors)
    && store.state.activeSubmission.authors.some((author: Author) => author.id === editedAuthor.id)
})
</script>
